import { Page } from "../../../components/page-base";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { Status, StatusChip } from "../../../components/datagrid/StatusChip";
import QualityBar from "../../../components/datagrid/QualityBar";
import { ClickableTextCell } from "../../../components/datagrid/ClickableTextCell";
import { toast } from "react-toastify";

import type {
  DataGridColumn,
  DataGridRow,
  FilterChip,
} from "../../../components/datagrid/types";
import SearchIcon from "@mui/icons-material/Search";
import {
  ChipFilterConfig,
  FilterDrawer,
} from "../../../components/filter-drawer";
import { BtnContainer, Container } from "./styles";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SyncIcon from "@mui/icons-material/Sync";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { EmailService } from "../services";
import useUserStore from "../../../store/user";
import { RowActionButtons } from "../../../components/datagrid/RowActionButtons";

type StatCard = {
  title: string;
  value: number;
  icon: ReactNode;
  color: string;
  iconColor: string;
};

const baseStats: StatCard[] = [
  {
    title: "Total",
    value: 0,
    icon: <BarChartIcon fontSize="large" />,
    color: "#e3f2fd",
    iconColor: "#1264C7",
  },
  {
    title: "Ativos",
    value: 0,
    icon: <CheckCircleIcon fontSize="large" />,
    color: "#e8f5e9",
    iconColor: "#1E8E3E",
  },
  {
    title: "Suspensos",
    value: 0,
    icon: <CancelIcon fontSize="large" />,
    color: "#fce4e4",
    iconColor: "#D93025",
  },
  {
    title: "Acesso Suspenso",
    value: 0,
    icon: <WarningIcon fontSize="large" />,
    color: "#fffde7",
    iconColor: "#fbbc05",
  },
  {
    title: "Cópia",
    value: 0,
    icon: <ContentCopyIcon fontSize="large" />,
    color: "#f3e5f5",
    iconColor: "#9c27b0",
  },
];

const statusDictionary: Record<string, { status: Status; label: string }> = {
  ACTIVE: { status: "success", label: "Ativo" },
  COMPLETED: { status: "success", label: "Concluído" },
  SUCCESS: { status: "success", label: "Concluído" },
  RUNNING: { status: "warning", label: "Em processamento" },
  PROCESSING: { status: "warning", label: "Processando" },
  PENDING: { status: "warning", label: "Pendente" },
  NOT_STARTED: { status: "warning", label: "Não iniciado" },
  COPY: { status: "warning", label: "Cópia" },
  COPIED: { status: "warning", label: "Cópia" },
  ERROR: { status: "disabled", label: "Erro" },
  FAILED: { status: "disabled", label: "Falha" },
  SUSPENDED: { status: "disabled", label: "Suspenso" },
  BLOCKED: { status: "disabled", label: "Bloqueado" },
  PARTIAL_FAILURE: { status: "disabled", label: "Falha parcial" },
  DUPLICATED: { status: "warning", label: "Duplicado" },
  FETCHING: { status: "warning", label: "Buscando perfis" },
  "AUTH-ERROR": { status: "danger", label: "Reconectar" },
};

const mapStatusToChip = (
  rawStatus?: string
): { status: Status; label: string } => {
  if (!rawStatus) {
    return { status: "null", label: "-" };
  }

  const normalized = rawStatus.toUpperCase();
  const mapped = statusDictionary[normalized];
  if (mapped) {
    return mapped;
  }

  return { status: "warning", label: rawStatus };
};

export interface EmailAccount {
  id: number;
  email: string;
  connectedProfiles: number;
  quality: number;
  lastSync: string;
  status: Status;
  statusLabel: string;
  rawStatus?: string;
}

export interface ImportedProfileItem {
  id: number;
  displayName?: string;
  googleLocationId?: string;
  status?: string;
  reason?: string;
  processedAt?: string;
}

type ImportsManagerResponseItem = {
  id?: number;
  email?: string;
  totalItems?: number;
  processedItems?: number;
  status?: string;
  connectedProfiles?: number;
  quality?: number;
  lastSync?: string;
};

const initialFilters: { status: Status | "all" } = {
  status: "all",
};

export const EmailPage = () => {
  const { user } = useUserStore();
  const token = user?.token ?? "";
  const location = useLocation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
  });
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isConnectingEmail, setIsConnectingEmail] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  type SortDirection = "asc" | "desc" | null;
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [profilesDialog, setProfilesDialog] = useState<{
    open: boolean;
    loading: boolean;
    email: string;
    items: ImportedProfileItem[];
  }>({ open: false, loading: false, email: "", items: [] });

  const handleSortConnectedProfiles = useCallback(() => {
    setSortDirection((prev) =>
      prev === null ? "desc" : prev === "desc" ? "asc" : null
    );
  }, []);

  const fetchEmailAccounts = useCallback(async () => {
    if (!token) {
      return;
    }

    setIsLoadingAccounts(true);

    try {
      const response = await EmailService.importsManager(token, sortDirection ?? undefined);

      if (Array.isArray(response)) {
        // Filtra resposta para garantir que temos dados válidos
        const validResponse = response.filter(
          (item) => item && item.email && item.email !== "-"
        );

        const formatted = (validResponse as ImportsManagerResponseItem[]).map(
          (item): EmailAccount => {
            const { status, label } = mapStatusToChip(item?.status);
            return {
              id: item?.id ?? 0,
              email: item?.email ?? "-",
              connectedProfiles: item?.connectedProfiles ?? 0,
              quality: item?.quality ?? 0,
              lastSync: item?.lastSync ?? "Nunca",
              status,
              statusLabel: label,
              rawStatus: item?.status,
            };
          }
        );
        if (formatted.some((acc) => acc.rawStatus === "AUTH-ERROR")) {
          toast.warning(
            "Atenção: Algumas contas precisam ser reconectadas. Por favor, realizar a reconexão de email."
          );
        }
        console.log(formatted);
        setAccounts(formatted);
      } else {
        setAccounts([]);
      }
    } catch {
      // Error handling already done in service
    } finally {
      setIsLoadingAccounts(false);
    }
  }, [token, sortDirection]);

  useEffect(() => {
    fetchEmailAccounts();
  }, [fetchEmailAccounts]);

  // Detecta se deve recarregar após OAuth
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("refresh") === "true") {
      // Remove o parâmetro da URL primeiro
      navigate("/email", { replace: true });

      // Aguarda um pequeno delay para garantir que o backend processou
      const timer = setTimeout(() => {
        fetchEmailAccounts();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [location.search, navigate, fetchEmailAccounts]);

  const handleConnectEmail = async () => {
    if (!user?.token) {
      toast.error("Por favor, realize login primeiro");
      return;
    }
    setIsConnectingEmail(true);
    try {
      await EmailService.oauthGoogle(user.token);
    } catch {
      setIsConnectingEmail(false);
    }
  };

  const filteredAccounts = useMemo(() => {
    let result = accounts;

    if (filters.status !== "all") {
      result = result.filter((acc) => acc.status === filters.status);
    }

    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter((acc) =>
        acc.email.toLowerCase().includes(lowerTerm)
      );
    }

    return result;
  }, [accounts, filters.status, searchTerm]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      totalCount: filteredAccounts.length,
      page: 1,
    }));
  }, [filteredAccounts.length]);

  const paginatedAccounts = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return filteredAccounts.slice(startIndex, endIndex);
  }, [filteredAccounts, pagination.page, pagination.pageSize]);

  const computedStats = useMemo(() => {
    return baseStats.map((stat) => {
      switch (stat.title) {
        case "Total":
          return { ...stat, value: accounts.length };
        case "Ativos":
          return {
            ...stat,
            value: accounts.filter((acc) => acc.status === "success").length,
          };
        case "Suspensos":
          return {
            ...stat,
            value: accounts.filter((acc) => acc.status === "disabled").length,
          };
        case "Cópia":
          return {
            ...stat,
            value: accounts.filter((acc) => acc.status === "warning").length,
          };
        default:
          return stat;
      }
    });
  }, [accounts]);

  const suspendedCount = useMemo(
    () => computedStats.find((stat) => stat.title === "Suspensos")?.value ?? 0,
    [computedStats]
  );

  const columns: DataGridColumn[] = useMemo(
    () => [
      { id: "email", header: "E-mail", minWidth: 240 },
      { id: "status", header: "Status", minWidth: 130 },
      {
        id: "connectedProfiles",
        minWidth: 160,
        header: (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            Perfis Conectados
            <Tooltip
              title={
                sortDirection === null
                  ? "Ordenar decrescente"
                  : sortDirection === "desc"
                    ? "Ordenar crescente"
                    : "Remover ordenação"
              }
            >
              <IconButton size="small" onClick={handleSortConnectedProfiles}>
                {sortDirection === "desc" ? (
                  <ArrowDownwardIcon fontSize="inherit" />
                ) : sortDirection === "asc" ? (
                  <ArrowUpwardIcon fontSize="inherit" />
                ) : (
                  <UnfoldMoreIcon fontSize="inherit" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      { id: "quality", header: "Qualidade", minWidth: 150 },
      { id: "lastSync", header: "Última Sincronização", minWidth: 180 },
      { id: "actions", header: "Ações", minWidth: 100 },
    ],
    [sortDirection, handleSortConnectedProfiles]
  );
  function formatToBRDateTime(isoString: string): string {
    if (!isoString || isoString === "Nunca") return "Nunca";
    const parts = isoString.split("T");
    if (parts.length < 2) return isoString;

    const [datePart, timePart] = parts;
    const [year, month, day] = datePart.split("-");
    const [hour, minute] = timePart.split(":");

    return `${day}/${month}/${year} ${hour}:${minute}`;
  }

  const closeProfilesDialog = useCallback(() => {
    setProfilesDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const openProfilesDialog = useCallback(
    async (account: EmailAccount) => {
      setProfilesDialog({
        open: true,
        loading: true,
        email: account.email,
        items: [],
      });
      const data = await EmailService.getManagerProfiles(
        account.id.toString(),
        token,
      );
      setProfilesDialog({
        open: true,
        loading: false,
        email: account.email,
        items: Array.isArray(data) ? (data as ImportedProfileItem[]) : [],
      });
    },
    [token],
  );

  const resyncEmailAccount = useCallback(
    async (id: string) => {
      const result = await EmailService.resyncManager(id);
      await fetchEmailAccounts();
      if (result?.success) {
        // O backend processa de forma assíncrona; recarrega novamente em seguida.
        setTimeout(() => {
          fetchEmailAccounts();
        }, 4000);
      }
    },
    [fetchEmailAccounts],
  );

  const rows: DataGridRow[] = useMemo(
    () =>
      paginatedAccounts.map((acc) => ({
        id: acc.id,
        render: {
          email: (
            <Typography variant="body2" fontWeight="medium">
              {acc.email}
            </Typography>
          ),
          status: <StatusChip status={acc.status} label={acc.statusLabel} />,
          connectedProfiles: (
            <ClickableTextCell
              text={String(acc.connectedProfiles)}
              onClick={() => openProfilesDialog(acc)}
            />
          ),
          quality: <QualityBar value={acc.quality} />,
          lastSync: (
            <Typography variant="body2" color="text.secondary">
              {formatToBRDateTime(acc.lastSync)}
            </Typography>
          ),
          actions: (
            <RowActionButtons
              actions={[
                {
                  icon: <SyncIcon fontSize="small" />,
                  tooltip: "Resincronizar",
                  onClick: () => resyncEmailAccount(acc.id.toString()),
                },
              ]}
            />
          ),
        },
      })),
    [paginatedAccounts, openProfilesDialog, resyncEmailAccount]
  );

  const filterChips = useMemo<FilterChip[]>(() => {
    if (filters.status === "all") {
      return [];
    }

    const labelForStatus: Record<Status, string> = {
      success: "Ativo",
      disabled: "Suspenso",
      warning: "Cópia",
      OK: "Ok",
      ATENCAO: "Atenção",
      CRITICO: "Crítico",
      null: "-",
      danger: "Reconectar",
      inactive: "Inativo",
      active: "Ativo",
    };

    return [
      {
        id: "status",
        label: `Status: ${labelForStatus[filters.status] ?? filters.status}`,
        onDelete: () => setFilters(initialFilters),
      },
    ];
  }, [filters.status]);

  const chipFilters: ChipFilterConfig[] = useMemo(
    () => [
      {
        id: "status",
        label: "Status",
        options: [
          { label: "Todos", value: "all" },
          { label: "Ativo", value: "success" },
          { label: "Suspenso", value: "disabled" },
          { label: "Cópia", value: "warning" },
          { label: "Reconectar", value: "danger" },
        ],
      },
    ],
    []
  );

  const handleDrawerFilterChange = useCallback(
    (field: string, value: Status | "all") => {
      if (field === "status") {
        setFilters({ status: value });
      }
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setPagination((p) => ({ ...p, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination((p) => ({ ...p, pageSize, page: 1 }));
  }, []);

  return (
    <Page.Root>
      <Container id="dashboard-container">
        <Page.HeaderAux />
        <BtnContainer>
          <Page.Header
            title="Conectar E-mail"
            subtitle="Gerencie contas de e-mail conectadas à API"
          />
          <div className="btn">
            <Page.Button
              label={
                isConnectingEmail ? "Redirecionando..." : "Conectar novo e-mail"
              }
              bgColor="#1264C7"
              hoverBgcolor="#1d4ed8"
              startIcon={<AddOutlinedIcon />}
              disabled={isConnectingEmail}
              onClick={handleConnectEmail}
            />
          </div>
        </BtnContainer>

        <Box
          sx={{
            width: "88%",
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Grid container spacing={3}>
            {computedStats.map((stat) => (
              // @ts-expect-error - Grid typings ainda exigem explicitamente `component`, mas layout padrão com div funciona bem.
              <Grid item xs={12} sm={6} md={3} key={stat.title}>
                <Card
                  sx={{
                    textAlign: "center",
                    backgroundColor: stat.color,
                    border: `1px solid ${stat.iconColor}33`,
                    borderRadius: "12px",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    width: "200px",
                  }}
                >
                  <CardContent>
                    <Box sx={{ color: stat.iconColor, mb: 1 }}>{stat.icon}</Box>
                    <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {suspendedCount > 0 && (
            <Alert
              severity="error"
              icon={<WarningIcon />}
              sx={{ borderRadius: "12px" }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  variant="contained"
                  startIcon={<SyncIcon />}
                  sx={{
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#c62828" },
                  }}
                >
                  Reautenticar Contas
                </Button>
              }
            >
              <AlertTitle>Atenção: Contas Suspensas Detectadas</AlertTitle>
              Detectamos <strong>{suspendedCount} contas suspensas</strong>.
              Reautentique para restaurar a sincronização e evitar perda de
              dados.
            </Alert>
          )}
          <AppDataGrid
            columns={columns}
            rows={rows}
            tableLabel="contas de e-mail"
            onOpenFilters={() => setIsFilterDrawerOpen(true)}
            filterChips={filterChips}
            filterCount={filterChips.length}
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
            noDataIcon={<SearchIcon sx={{ fontSize: 48 }} />}
            noDataTitle="Nenhuma conta encontrada"
            noDataDescription="Ajuste sua busca ou adicione novas contas de e-mail."
            isLoading={isLoadingAccounts}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            searchPlaceholder="Buscar por e-mail..."
          />
        </Box>
        <FilterDrawer
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFilterChange={handleDrawerFilterChange}
          onClearFilters={handleClearFilters}
          chipFilters={chipFilters}
        />
        <Dialog
          open={profilesDialog.open}
          onClose={closeProfilesDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Perfis do e-mail {profilesDialog.email}</DialogTitle>
          <DialogContent dividers>
            {profilesDialog.loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : profilesDialog.items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum perfil encontrado para este lote.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Perfil</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Motivo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {profilesDialog.items.map((item) => {
                    const chip = mapStatusToChip(item.status);
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          {item.displayName || item.googleLocationId || "-"}
                        </TableCell>
                        <TableCell>
                          <StatusChip status={chip.status} label={chip.label} />
                        </TableCell>
                        <TableCell>{item.reason || "-"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeProfilesDialog}>Fechar</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page.Root>
  );
};
